import React from 'react';
import { LucideIcon } from 'lucide-react';
import { useTranslatedContent } from '../../../i18n/hooks/useTranslatedContent';
import Button from '../../common/Button';
import type { SaaSProductTier } from '../../../types/saas/products';

interface SaaSProductProps {
  icon: LucideIcon;
  title: string;
  description: string;
  tiers: {
    starter: SaaSProductTier;
    business: SaaSProductTier;
    enterprise: SaaSProductTier;
  };
}

const SaaSProduct: React.FC<SaaSProductProps> = ({
  icon: Icon,
  title,
  description,
  tiers
}) => {
  const { t } = useTranslatedContent('saas');

  const TierCard: React.FC<{
    tier: SaaSProductTier;
    isHighlighted?: boolean;
  }> = ({ tier, isHighlighted }) => (
    <div className={`p-6 h-full flex flex-col ${isHighlighted ? 'bg-blue-50' : ''}`}>
      <div className="text-center">
        <h4 className="text-lg font-semibold text-gray-900">{tier.title}</h4>
        <p className="mt-4 text-gray-600">{tier.subtitle}</p>
      </div>
      <ul className="mt-6 space-y-4 flex-grow">
        {tier.features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <svg className="h-5 w-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            <span className="ml-3 text-gray-600">{feature}</span>
          </li>
        ))}
      </ul>
      <div className="mt-8">
        <Button 
          variant="primary" 
          className="w-full"
          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
        >
          {t('buttons.getQuote')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
      <div className="px-6 py-8 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="flex items-center">
          <Icon className="h-8 w-8 text-white" />
          <div className="ml-4">
            <h3 className="text-2xl font-bold text-white">{title}</h3>
            <p className="text-blue-100">{description}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x">
        <TierCard tier={tiers.starter} />
        <TierCard tier={tiers.business} isHighlighted />
        <TierCard tier={tiers.enterprise} />
      </div>
    </div>
  );
};

export default SaaSProduct;